<script lang="ts">
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { isMobile } from "$/utils/isMobile";
    import SidePanel from "../SidePanel.svelte";
    import Card from "$/components/card/Card.svelte";

    import { snippetSidePanelStore } from "$/stores/snippetSidePanelStore";

    import type { DashboardViewManagerProps } from "./types";
    import PanelHeader from "../panels/_components/PanelHeader.svelte";
    import { AppStore, AppType, Panels } from "$/stores/AppStore";
    import {
        destroyDragingCardGhostElement,
        generateDraggingCardGhostAndSetCanCopy,
        getRootDocument,
        makeElementImagesDraggable,
    } from "$/utils/Utils";
    import { onDestroy, onMount } from "svelte";
    import Sortable from "sortablejs";
    import { fade } from "svelte/transition";

    export let props: DashboardViewManagerProps = {
        userTopicRole: TopicAccessLevel.NO_ACCESS,
        selectedTagNames: [],
        user: {},
        hideReactions: false,
    };

    // start of scroll left/top and right/bottom on dragenter and dragleave
    let snippetsWrapper: HTMLDivElement;
    let scrollInterval;
    let sortableDragAndDrop;

    let leftTopScrollHover = false;

    $: selectedSnippet = $ActiveTopic && ActiveTopicStore.getSnippet($snippetSidePanelStore?.snippet?.id);

    function scrollLeftTop() {
        sortableDragAndDrop.option("disabled", true);
        leftTopScrollHover = true;
        scrollInterval = setInterval(() => {
            snippetsWrapper.scrollTop -= 10;
            if (snippetsWrapper.scrollTop === 0) stopScroll();
        }, 10);
    }

    let rightBottomScrollHover = false;

    function scrollRightBottom() {
        sortableDragAndDrop.option("disabled", true);
        rightBottomScrollHover = true;
        scrollInterval = setInterval(() => {
            snippetsWrapper.scrollTop += 10;
            if (
                snippetsWrapper.scrollTop ===
                snippetsWrapper.scrollHeight - snippetsWrapper.clientHeight
            )
                stopScroll();
        }, 10);
    }

    function stopScroll() {
        leftTopScrollHover = false;
        rightBottomScrollHover = false;
        sortableDragAndDrop.option("disabled", false);
        clearInterval(scrollInterval);
        scrollInterval = null;
    }

    // end of scroll left/top and right/bottom on dragenter and dragleave

    $: $snippetSidePanelStore, openSnippetPreview();

    const openSnippetPreview = () => {
        const openSnippet = $ActiveTopic.snippets.find(
            (snippet) => snippet.id === $snippetSidePanelStore.snippet?.id,
        );
        if (openSnippet && $AppStore.panel === Panels.SNIPPET)
            snippetSidePanelStore.setSnippet(openSnippet);
    };

    const sortableChosenClass: string = "sortable-chosen";

    const dragAndDropConfig = {
        handle: ".card-header",
        chosenClass: sortableChosenClass,
        animation: 150,
        delay: 200,
        delayOnTouchOnly: true,
        onStart(event) {
            // Add quick vibration feedback when drag starts
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }
        },
        onEnd(event) {
            // When the user is dragging a snippet at the very top or bottom
            // of the page and drops the snippet the dragleave event won't fire so if
            // the scroll is still active after we drop the snippet,
            // stop the scroll and return the element to it's original position
            if (scrollInterval) {
                const items = event.from.querySelectorAll(":scope > div");
                event.from.insertBefore(event.item, items[event.oldIndex]);
                stopScroll();
                return false;
            }

            // SortableJS is setting images draggable attribute to false so we need to reset it after drop
            makeElementImagesDraggable(event.item);

            ActiveTopicStore.changeSnippetPosition(
                event.oldIndex,
                event.newIndex,
            );
        },
    };

    const setDragAndDropPermission = () => {
        sortableDragAndDrop.option(
            "disabled",
            props.userTopicRole < TopicAccessLevel.EDITOR,
        );
    };

    const dragCardEventListener = (event: DragEvent) => {
        generateDraggingCardGhostAndSetCanCopy(
            AppType.DASHBOARD,
            event,
            sortableChosenClass,
        );
    };

    // setting drag and drop permission after sortableDragAndDrop initialization
    $: sortableDragAndDrop, sortableDragAndDrop && setDragAndDropPermission();

    // enabling snippet's content copy from tab to tab (drag and drop)
    onMount(() => {
        sortableDragAndDrop = Sortable.create(
            snippetsWrapper,
            dragAndDropConfig,
        );
        getRootDocument().addEventListener("dragstart", dragCardEventListener);
        getRootDocument().addEventListener(
            "dragend",
            destroyDragingCardGhostElement,
        );
    });

    onDestroy(() => {
        getRootDocument().removeEventListener(
            "dragstart",
            dragCardEventListener,
        );
        getRootDocument().removeEventListener(
            "dragend",
            destroyDragingCardGhostElement,
        );
    });
</script>

<div class="snippets_wrapper sm:mt-4" bind:this={snippetsWrapper} in:fade|local>
    {#each $ActiveTopic?.snippets ?? [] as snippet, index (snippet.id)}
        <div class="card_wrapper">
            <Card
                card={snippet}
                editRole={props.userTopicRole}
                user={props.user}
                topicId={$ActiveTopic.id}
                deleteSnippet={ActiveTopicStore.deleteSnippet}
                createComment={ActiveTopicStore.createComment}
                updateComment={ActiveTopicStore.updateComment}
                deleteComment={ActiveTopicStore.deleteComment}
                openInNewTab={true}
                isGridCard={!$isMobile}
                hideReactions={props.hideReactions}
            />
        </div>
    {/each}
    <div
        class="cw-invisible-scroll-bar cw-invisible-top"
        class:cw-invisible-scroll-bar--hover={leftTopScrollHover}
        on:dragenter={scrollLeftTop}
        on:dragleave={stopScroll}
    ></div>
    <div
        class="cw-invisible-scroll-bar cw-invisible-bottom"
        class:cw-invisible-scroll-bar--hover={rightBottomScrollHover}
        on:dragenter={scrollRightBottom}
        on:dragleave={stopScroll}
    ></div>
</div>

<SidePanel show={$AppStore.panel === Panels.SNIPPET && !$isMobile}>
    <div class="side_pannel_wrapper">
        <PanelHeader
            title={"Preview snippet"}
            on:close={AppStore.closePanels}
        />

        {#if $snippetSidePanelStore.snippet}
            <div class="side_pannel_card_wrapper">
                <Card
                    bgColor="#f3f4f6"
                    card={selectedSnippet}
                    createComment={ActiveTopicStore.createComment}
                    deleteComment={ActiveTopicStore.deleteComment}
                    deleteSnippet={() => {
                        ActiveTopicStore.deleteSnippet(
                            $snippetSidePanelStore.snippet.id,
                        );
                        AppStore.closePanels();
                    }}
                    editRole={props.userTopicRole}
                    openInNewTab
                    topicId={$ActiveTopic.id}
                    updateComment={ActiveTopicStore.updateComment}
                    user={props.user}
                    isGridCard={false}
                    hideReactions={props.hideReactions}
                />
            </div>
        {/if}
    </div>
</SidePanel>

<style>
    .header_wrapper {
        @apply sticky top-0 z-[100] !important;
    }

    .snippets_wrapper {
        @apply flex flex-col gap-4 overflow-y-auto px-4 pb-28 md:grid md:grid-cols-grid-view !important;
    }

    .card_wrapper {
        @apply z-20 w-full md:relative;
    }

    .card_wrapper::-webkit-scrollbar {
        @apply h-3 w-3;
    }

    .side_pannel_wrapper {
        @apply flex h-full w-full flex-col overflow-auto bg-gray-100;
    }

    .card_wrapper::-webkit-scrollbar-thumb {
        @apply bg-transparent;
    }

    .card_wrapper:hover::-webkit-scrollbar-thumb {
        @apply bg-gray-200 hover:bg-gray-300;
    }

    .side_pannel_card_wrapper {
        @apply h-full overflow-y-auto px-4 pb-20 pt-0;
    }

    .cw-invisible-scroll-bar {
        @apply fixed z-40 h-0 w-0 bg-transparent;
    }

    .cw-invisible-scroll-bar--hover {
        @apply h-20 w-full !important;
    }

    .cw-invisible-top {
        @apply left-0 top-[8rem];
    }

    .cw-invisible-bottom {
        @apply bottom-0 right-0;
    }
</style>
